import React, { useState } from 'react';
import { Car } from '../types';
import { useBrandById, useModelById, useSupplierById, useStore } from '../store-hybrid';
import { BrandLogo } from './BrandLogo';
import { ExtendRentalModal } from './ExtendRentalModal';
import { handleImageError } from '../utils/imageUtils';
import { formatSimpleCurrency } from '../utils/currencyUtils';

interface CarCardProps {
  car: Car;
  onEdit?: (car: Car) => void;
  onDelete?: (carId: string) => void;
}

export const CarCard: React.FC<CarCardProps> = ({ car, onEdit, onDelete }) => {
  const brand = useBrandById(car.brandId);
  const model = useModelById(car.modelId);
  const supplier = useSupplierById(car.supplierId || '');
  const { extendRental } = useStore();
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const hasMultipleImages = car.images && car.images.length > 1;
  const totalImages = car.images?.length || 0;

  const nextImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev + 1) % totalImages);
    }
  };

  const prevImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  // Helper function to get rental status for notifications
  const getRentalStatus = () => {
    if (!car.isRented || !car.rentedUntil) return null;
    
    const now = new Date();
    const rentedUntil = new Date(car.rentedUntil);
    const msUntilReturn = rentedUntil.getTime() - now.getTime();
    const hoursUntilReturn = msUntilReturn / (1000 * 60 * 60);
    const minutesUntilReturn = msUntilReturn / (1000 * 60);
    const daysUntilReturn = msUntilReturn / (1000 * 60 * 60 * 24);
    
    if (hoursUntilReturn < 0) {
      return { 
        type: 'overdue', 
        hours: Math.abs(hoursUntilReturn),
        minutes: Math.abs(minutesUntilReturn),
        days: Math.abs(daysUntilReturn),
        formatted: formatTimeRemaining(msUntilReturn, true)
      };
    } else if (hoursUntilReturn <= 1) {
      return { 
        type: 'due-very-soon', 
        hours: hoursUntilReturn,
        minutes: minutesUntilReturn,
        days: daysUntilReturn,
        formatted: formatTimeRemaining(msUntilReturn, false)
      };
    } else if (hoursUntilReturn <= 6) {
      return { 
        type: 'due-soon', 
        hours: hoursUntilReturn,
        minutes: minutesUntilReturn,
        days: daysUntilReturn,
        formatted: formatTimeRemaining(msUntilReturn, false)
      };
    } else if (hoursUntilReturn <= 24) {
      return { 
        type: 'due-today', 
        hours: hoursUntilReturn,
        minutes: minutesUntilReturn,
        days: daysUntilReturn,
        formatted: formatTimeRemaining(msUntilReturn, false)
      };
    }
    return null;
  };

  // Helper function to format time remaining
  const formatTimeRemaining = (ms: number, isOverdue: boolean) => {
    const absMs = Math.abs(ms);
    const days = Math.floor(absMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((absMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((absMs % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h ${isOverdue ? 'OVERDUE' : 'LEFT'}`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${isOverdue ? 'OVERDUE' : 'LEFT'}`;
    } else {
      return `${minutes}m ${isOverdue ? 'OVERDUE' : 'LEFT'}`;
    }
  };

  const rentalStatus = getRentalStatus();

  const handleExtendRental = (newEndDate: Date, newEndTime: string) => {
    extendRental(car.id, newEndDate, newEndTime);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-md transition-all duration-300 relative">
      {/* Enhanced Rental Status Notification */}
      {rentalStatus && (
        <div className={`absolute top-2 right-2 px-3 py-2 rounded-lg text-xs font-bold z-10 shadow-lg border-2 ${
          rentalStatus.type === 'overdue' 
            ? 'bg-red-600 text-white animate-pulse border-red-700 shadow-red-200'
            : rentalStatus.type === 'due-very-soon'
              ? 'bg-red-500 text-white animate-bounce border-red-600 shadow-red-200'
              : rentalStatus.type === 'due-soon'
                ? 'bg-orange-500 text-white border-orange-600 shadow-orange-200'
                : 'bg-yellow-500 text-white border-yellow-600 shadow-yellow-200'
        }`}>
          <div className="text-center">
            <div className="text-xs opacity-90 mb-1">
              {rentalStatus.type === 'overdue' 
                ? '🚨 OVERDUE'
                : rentalStatus.type === 'due-very-soon'
                  ? '⚠️ URGENT'
                  : rentalStatus.type === 'due-soon'
                    ? '⏰ DUE SOON'
                    : '📅 DUE TODAY'
              }
            </div>
            <div className="font-extrabold">
              {rentalStatus.formatted}
            </div>
          </div>
        </div>
      )}

      {/* Car Image */}
      <div className="w-full h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl mb-4 overflow-hidden relative group">
        {car.images && car.images.length > 0 ? (
          <>
            <img 
              src={car.images[currentImageIndex]} 
              alt={`${brand?.name} ${model?.name}`}
              className="w-full h-full object-cover transition-opacity duration-300"
              onError={(e) => handleImageError(e, 'car', 400, 300)}
            />
            
            {/* Navigation Buttons */}
            {hasMultipleImages && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-opacity-70"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                {/* Image Counter */}
                <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  {currentImageIndex + 1}/{totalImages}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>

      {/* Car Info */}
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <BrandLogo brandId={car.brandId} size="md" />
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">
                {brand?.name} {model?.name}
              </h3>
              {car.year && (
                <p className="text-sm text-gray-500">{car.year}</p>
              )}
            </div>
          </div>
          
          {car.rating && (
            <div className="flex items-center space-x-1">
              <svg className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{car.rating}</span>
            </div>
          )}
        </div>

        {/* Car Details */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          {car.transmission && (
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4" />
              </svg>
              <span className="text-gray-600 dark:text-gray-400 dark:text-gray-400 capitalize">{car.transmission}</span>
            </div>
          )}
          
          {car.fuel && (
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
              </svg>
              <span className="text-gray-600 dark:text-gray-400 capitalize">{car.fuel}</span>
            </div>
          )}
        </div>

        {/* Purchase Price and Supplier */}
        {(car.purchasePrice || supplier) && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            {car.purchasePrice && (
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                <span className="text-gray-600 dark:text-gray-400">Purchase: {formatSimpleCurrency(car.purchasePrice)}</span>
              </div>
            )}
            
            {supplier && (
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span className="text-gray-600 dark:text-gray-400">{supplier.name}</span>
              </div>
            )}
          </div>
        )}

        {car.plate && (
          <div className="flex items-center space-x-2 text-sm">
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <span className="text-gray-600 dark:text-gray-400">{car.plate}</span>
          </div>
        )}

        {/* Status and Pricing */}
        <div className="pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
          {/* Status */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                car.isRented ? 'bg-orange-500' : 
                car.isSold ? 'bg-red-500' : 
                'bg-success-500'
              }`}></div>
              <span className={`text-sm font-medium ${
                car.isRented ? 'text-orange-600' : 
                car.isSold ? 'text-red-600' : 
                'text-success-600'
              }`}>
                {car.isRented ? 'Out of Garage' : car.isSold ? 'Out of Garage' : 'Available'}
              </span>
            </div>
            
            {car.inGarage && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                In Garage
              </span>
            )}
          </div>
          
          {/* Pricing */}
          <div className="flex justify-between items-center">
            {car.dailyRate && (
              <div className="text-sm">
                <span className="font-semibold text-blue-600">{formatSimpleCurrency(car.dailyRate || 0)}</span>
                <span className="text-gray-500">/day</span>
              </div>
            )}
            {car.salePrice && (
              <div className="text-sm font-semibold text-green-600">
                {formatSimpleCurrency(car.salePrice || 0)}
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-2 pt-3">
          {/* Enhanced Extend Rental Button - Show for rented cars with notification */}
          {car.isRented && rentalStatus && (
            <button
              onClick={() => setShowExtendModal(true)}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                rentalStatus.type === 'overdue' 
                  ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse shadow-lg'
                  : rentalStatus.type === 'due-very-soon'
                    ? 'bg-red-500 text-white hover:bg-red-600 animate-bounce shadow-md'
                    : rentalStatus.type === 'due-soon'
                      ? 'bg-orange-500 text-white hover:bg-orange-600 shadow-md'
                      : 'text-orange-600 bg-orange-50 hover:bg-orange-100'
              }`}
            >
              {rentalStatus.type === 'overdue' 
                ? '🚨 Extend Now!'
                : rentalStatus.type === 'due-very-soon'
                  ? '⚠️ Extend Urgent'
                  : '⏰ Extend Rental'
              }
            </button>
          )}
          
          {/* Regular Actions */}
          {onEdit && (
            <button
              onClick={() => onEdit(car)}
              className={`${car.isRented && rentalStatus ? 'flex-1' : 'flex-1'} px-3 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors`}
            >
              Edit
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(car.id)}
              className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Extend Rental Modal */}
      {car.isRented && car.rentedUntil && (
        <ExtendRentalModal
          isOpen={showExtendModal}
          onClose={() => setShowExtendModal(false)}
          onExtend={handleExtendRental}
          currentEndDate={new Date(car.rentedUntil)}
          carInfo={{
            brand: brand?.name || '',
            model: model?.name || '',
            plate: car.plate
          }}
        />
      )}
    </div>
  );
};
